import { FormattedMessage, useIntl } from 'react-intl';
import React from 'react';
import bemify from 'bemify-js';

import Link from 'app/common/components/Link';

import { StaticImage } from '@zapier/common-components';

import Code from '../components/Code';
import Paragraph from '../components/Paragraph';
import StepTitle from '../components/StepTitle';
import SyntaxHighlighter from '../components/SyntaxHighlighter';

const bem = bemify('handling-authentication');

const jsCodeBlock = `const getAccessToken = async (z, bundle) => {
  const response = await z.request({
    url: 'https://github.com/login/oauth/access_token',
    method: 'POST',
    body: {
      client_id: process.env.CLIENT_ID,
      client_secret: process.env.CLIENT_SECRET,
      grant_type: 'authorization_code',
      code: bundle.inputData.code,
    },
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
  });
  return {
    access_token: response.data.access_token,
  };
};

const includeBearerToken = (request, z, bundle) => {
  if (bundle.authData.access_token) {
    request.headers.Authorization = Bearer \${bundle.authData.access_token};
  }

  return request;
};

const test = (z, bundle) => z.request({ url: 'https://api.github.com/user' });

module.exports = {
  config: {
    type: 'oauth2',
    oauth2Config: {
      authorizeUrl: {
        url: 'https://github.com/login/oauth/authorize',
        params: {
          client_id: '{{process.env.CLIENT_ID}}',
          state: '{{bundle.inputData.state}}',
          redirect_uri: '{{bundle.inputData.redirect_uri}}',
          response_type: 'code',
        },
      },
      getAccessToken,
    },

    fields: [],

    test,
    connectionLabel: '{{json.login}}',
  },
  befores: [includeBearerToken],
  afters: [],
};`;

const HandlingAuthentication = () => {
  const intl = useIntl();

  return (
    <div className={bem()}>
      <StepTitle>
        <FormattedMessage
          defaultMessage="Explore the Project: Handling Authentication"
          id="headings-explore-project-handling-authentication"
        />
      </StepTitle>
      <Paragraph>
        <FormattedMessage
          defaultMessage="The last bit we'll look at here is authentication. The app defines how the platform authenticates with your API, and what data needs to be collected from the user when they set up their Zap. The GitHub example uses Oauth 2.0 which matches users’ expectations for modern app authentication. OAuth 2.0 authentication lets users sign into app accounts and allows Zapier access via a popup window from that app. The app sends an authorization code when the user authenticates, which Zapier exchanges for an access token that is used to authenticate subsequent calls. The functionality to refresh that token if/when it expires is optional and since GitHub doesn't support it, you'll notice this tutorial app's authentication does not include a call to refresh the token."
          id="descriptions-oauth-authentication-overview"
        />
      </Paragraph>
      <Paragraph>
        <FormattedMessage
          defaultMessage="In the"
          id="descriptions-authentication-file-testing-prefix"
        />{' '}
        <Code>authentication.js</Code>{' '}
        <FormattedMessage
          defaultMessage="file, you'll notice the other thing we do here is specify an endpoint to use for testing the credentials the user enters. We also provide a label here, so the user sees in the UI which login they used when they connected."
          id="descriptions-authentication-file-testing-suffix"
        />
      </Paragraph>
      <Paragraph>
        <FormattedMessage
          defaultMessage="This authenticaton method requires some setup within GitHub. Use the"
          id="descriptions-github-auth-setup-prefix"
        />{' '}
        <Code>
          <FormattedMessage
            defaultMessage="zapier describe"
            id="labels-command-zapier-describe"
          />
        </Code>{' '}
        <FormattedMessage
          defaultMessage="command to find out what the Redirect URI is and copy it to add to the allowlist in GitHub’s interface, where this field is called the authorization callback URL. The Redirect URI will be available once you"
          id="descriptions-github-redirect-uri-setup-middle"
        />{' '}
        <Code>
          <FormattedMessage
            defaultMessage="zapier push"
            id="labels-command-zapier-push"
          />
        </Code>{' '}
        <FormattedMessage
          defaultMessage="so you'll want to come back to this step!"
          id="descriptions-github-redirect-uri-setup-suffix"
        />
      </Paragraph>
      <StaticImage
        alt={intl.formatMessage({
          id: 'captions-github-redirect-uri-screenshot-alt',
          defaultMessage: 'github redirect uri screenshot',
        })}
        src="https://cdn.zappy.app/da0ba8d455d48bf784033ec885945a15.png"
        title={intl.formatMessage({
          id: 'captions-github-redirect-uri-screenshot-title',
          defaultMessage: 'github redirect uri screenshot',
        })}
      />
      <Paragraph>
        <FormattedMessage
          defaultMessage="Finally you'll also want to set your"
          id="descriptions-github-client-credentials-prefix"
        />{' '}
        <Code>
          <FormattedMessage defaultMessage="CLIENT_ID" id="labels-client-id" />
        </Code>{' '}
        <FormattedMessage
          defaultMessage="and"
          id="descriptions-github-client-credentials-connector"
        />{' '}
        <Code>
          <FormattedMessage
            defaultMessage="CLIENT_SECRET"
            id="labels-client-secret"
          />
        </Code>{' '}
        <FormattedMessage
          defaultMessage="as environment variables with the command"
          id="descriptions-github-env-set-command-prefix"
        />{' '}
        <Code>
          <FormattedMessage
            defaultMessage="zapier env:set 1.0.0"
            id="labels-command-zapier-env-set-1-0-0"
          />
        </Code>
        <FormattedMessage
          defaultMessage=". In your new application’s developer settings in GitHub, you’ll receive these credentials that Zapier will use to verify itself with the authorization URL to receive the request token from GitHub."
          id="descriptions-github-client-credentials-suffix"
        />
      </Paragraph>
      <StaticImage
        alt={intl.formatMessage({
          id: 'captions-github-client-id-screenshot-alt',
          defaultMessage: 'github client id screenshot',
        })}
        src="https://cdn.zappy.app/69380b9f4dd69169b2fd15ad0c7ebaee.png"
        title={intl.formatMessage({
          id: 'captions-github-client-id-screenshot-title',
          defaultMessage: 'github client id screenshot',
        })}
      />
      <SyntaxHighlighter language="bash">{jsCodeBlock}</SyntaxHighlighter>
      <Paragraph>
        <FormattedMessage
          defaultMessage="If your API uses <component0>another authentication scheme,</component0> we've got loads of help for you in our docs."
          id="descriptions-other-auth-schemes-docs-link"
          values={{
            component0: chunks => (
              <Link
                href="https://github.com/zapier/zapier-platform/blob/main/packages/cli/README.md#authentication"
                target="_blank"
              >
                {chunks}
              </Link>
            ),
          }}
        />
      </Paragraph>
    </div>
  );
};

export default HandlingAuthentication;
